import { EntityTypes } from 'constants/entities';
import { IPreviewGroup, IPreviewVariable, updatePreview } from 'core/slices/preview';
import { put, call, all, select } from 'redux-saga/effects';
import {
  CourseNodeRole,
  CourseNodeType,
  ICourseNodeDAO,
  ICourseNodeEntity,
  ICourseNodeEntityDAO,
  ICourseOut,
  ICourseVariable,
} from 'types/domain/course';
import { previewCourseNode } from './preview';
import { loadModule } from '../module/load';
import { IActionFeed, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { startModule } from '../module/start';
import { selectOfflineId } from 'core/selectors/admin/offlineId';
import { generateOfflineId } from 'core/slices/offline';
import { selectCourseNode } from 'core/selectors/admin/courseNode';
import { RootState } from 'types';
import { IVariable, IVariableOption, VariableType } from 'types/domain/variable';
import { Handles } from 'constants/handles';
import { IKeyArray } from 'types/IKeyArray';
import {
  IVariableResult,
  IVerificationEntity,
  IVerificationRequest,
  IVerificationRequestEntity,
  VerificationRequestType,
  VerificationType,
} from 'types/domain/verification';
import { update } from 'core/actions/admin/data';
import { CertificateStatus, ICertificateDAO } from 'types/domain/certificate';
import { selectCertificate } from 'core/selectors/admin/certificate';
import { updateState } from 'core/actions/product/state';
import {
  DialogueRegisterState,
  IHistoryItem,
  IRegisterReducer,
  next,
  queue,
  registerDialogue,
} from 'core/slices/register';
import { VerificationRequestStatus } from 'types/domain/verification';
import { IEntityReducer } from 'core/reducers/entities';
import { IName } from 'types/session/auth';
import { selectRegister } from 'core/selectors/product/register';
import { IModuleVersion } from 'types/domain/module';
import { selectModuleVersions } from 'core/selectors/admin/moduleVersions';
import { reduceArrayById } from 'utils/reduceArrayById';
import { P } from 'app/pages/NotFoundPage/P';

export function* processModule(courseNode: ICourseNodeDAO) {
  let moduleId = courseNode.moduleId;

  if (courseNode.moduleVersionId) {
    const moduleVersion: IModuleVersion = yield select(
      (state: RootState) => state.entity.present[EntityTypes.MODULE_VERSION][courseNode.moduleVersionId],
    );

    if (moduleVersion != null && moduleVersion.status > -1) moduleId = moduleVersion.moduleId;
  }

  const moduleVersions: IModuleVersion[] = yield select(state => selectModuleVersions(state, courseNode.moduleId));
  const groups: IKeyArray<IPreviewGroup> = yield select((state: RootState) => state.preview.groups);
  const version = moduleVersions.find(mv => !!groups[mv.groupId]);

  if (version != null) moduleId = version.moduleId;

  yield loadModule(moduleId);
  const offlineId = yield select(selectOfflineId);
  const courseProgressId = yield select((state: RootState) => state.state[EntityTypes.COURSE_PROGRESS]);

  yield put(
    yield call(updatePreview, {
      type: EntityTypes.MODULE,
      id: moduleId,
      data: { sessionAttemptId: offlineId, courseProgressId },
    }),
  );

  yield put(yield call(generateOfflineId, null));
 
  yield put(
    yield call(queue, {
      moduleId: moduleId,
      sessionAttemptId: offlineId,
      courseProgressId,
    } as IHistoryItem),
  );
  const feed: IActionFeed = yield select(selectActionFeed);
  const register: IRegisterReducer = yield select(selectRegister);
  const current = feed.feed[feed.index];
  if (current && current.moduleId === moduleId) yield call(startModule, offlineId, moduleId, current.actionId);

   const nextItem = feed.feed[feed.index + 1];
  if (feed.index === 0 && nextItem && nextItem.moduleId === moduleId && register.isNext) yield call(startModule, offlineId, moduleId, nextItem.actionId);
}

export function* processDialogue(courseNode: ICourseNodeDAO) {
  const courseProgressId = yield select((state: RootState) => state.state[EntityTypes.COURSE_PROGRESS]);

  if (courseNode.entityType === CourseNodeRole.SYSTEM && !courseNode.organisationSystemId) return;

  yield put(
    yield call(updatePreview, {
      type: EntityTypes.COURSE_NODE,
      id: courseNode.id,
      data: { courseProgressId },
    }),
  );
  yield put(yield call(queue, { courseNodeId: courseNode.id, courseProgressId } as IHistoryItem));

}

export function* processVerification(courseNode: ICourseNodeDAO) {
  const id = yield select(selectOfflineId);
  const courseProgressId = yield select((state: RootState) => state.state[EntityTypes.COURSE_PROGRESS]);
  const entity: Partial<IVerificationRequest> = {
    textId: courseNode.textId,
    imageId: courseNode.imageId,
    iconId: courseNode.iconId,
    organisationUserCertificateId: id,
    certificateId: courseNode.certificateId,
    verificationId: courseNode.verificationId,
    courseProgressId,
    type: courseNode.certificateId ? VerificationRequestType.CERTIFICATE_ASSIGNED : VerificationRequestType.RESPONSE,
    id,
  };
  yield put(yield call(update, { type: EntityTypes.VERIFICATION_REQUEST, entity, persist: false }));
  yield put(yield call(updatePreview, { type: EntityTypes.VERIFICATION_REQUEST, id, data: { courseProgressId } }));
  yield put(yield call(queue, { verificationRequestId: id, courseProgressId } as IHistoryItem));

  const previewVariables: IKeyArray<IPreviewVariable> = yield select((state: RootState) => state.preview.variables);
  const variableOptions: IKeyArray<IVariableOption> = yield select(
    (state: RootState) => state.entity.present[EntityTypes.VARIABLE_OPTION],
  );
  const variables: IKeyArray<IVariable> = yield select(
    (state: RootState) => state.entity.present[EntityTypes.VARIABLE],
  );

  if (courseNode.verification)
    switch (courseNode.verification.type) {
      case VerificationType.EMAIL:
        return yield processNextNode(courseNode.id);
      case VerificationType.PROGRESS_CERTIFICATE:
        const certificate: ICertificateDAO = yield select((state: RootState) =>
          selectCertificate(state, courseNode.certificateId),
        );

        entity.certificateId = courseNode.certificateId;

        // const entities = yield all(
        //   certificate.variableGoals.map((ent: ICourseVariable, index) => {
        //     const entity: Partial<IVerificationRequestEntity> = {
        //       verificationRequestId: id,
        //       id: id - index,
        //       pass: ent.value === 1,
        //       value: ent.value,
        //       optionName: variableOptions[ent.variableOptionId]?.name,
        //       result: previewVariables[ent.variableId] as unknown as IVariableResult,
        //     };
        //     return call(update, { type: EntityTypes.VERIFICATION_REQUEST_ENTITY, entity, persist: false });
        //   }),
        // );
        // yield all(entities.map(ent => put(ent)));
        //yield put(yield call(generateOfflineId, entities.length));
        yield processNextNode(courseNode.id);
        break;
      case VerificationType.PROGRESS_OBJECTIVE:
        const objectiveEntities = yield all(
          courseNode.verification.entities
            .filter(e => e.status > -1 && e.courseNodeId === courseNode.id)
            .map((ent: IVerificationEntity, index) => {
              const entity: Partial<IVerificationRequestEntity> = {
                verificationRequestId: id,
                id: id - index,
                name: ent.name,
                type: variables[ent.variableId]?.type,
                result: previewVariables[ent.variableId] as unknown as IVariableResult,
              };
              return call(update, { type: EntityTypes.VERIFICATION_REQUEST_ENTITY, entity, persist: false });
            }),
        );
        yield all(objectiveEntities.map(ent => put(ent)));
        yield put(yield call(generateOfflineId, objectiveEntities.length));
        yield processNextNode(courseNode.id);
        break;
      case VerificationType.FEEDBACK:
      case VerificationType.OPTIONS:
        const courseNodeEntities = yield all(
          courseNode.verification.entities
            .filter(e => e.status > -1 && e.courseNodeId === courseNode.id)
            .map((ent: IVerificationEntity, index) => {
              const entity: Partial<IVerificationRequestEntity> = {
                verificationRequestId: id,
                id: id - index,
                verificationEntityId: ent.id,
                name: ent.name,
              };
              return call(update, { type: EntityTypes.VERIFICATION_REQUEST_ENTITY, entity, persist: false });
            }),
        );

        yield all(courseNodeEntities.map(ent => put(ent)));
        yield put(yield call(generateOfflineId, courseNodeEntities.length));

        if (courseNode.verification.type === VerificationType.FEEDBACK) yield processNextNode(courseNode.id);
        break;
    }
  //Create certificate dialogues
  else if (courseNode.certificateId) {
    const certificate: ICertificateDAO = yield select((state: RootState) =>
      selectCertificate(state, courseNode.certificateId),
    );

    // const orgUserCert: Partial<IOrgUserCertificate> = {
    //   id,
    //   certificateId: courseNode.certificateId,
    //   status: CertificateStatus.NEW,
    //   token: id,
    //   //issuedDate:
    // };
    // yield put(yield call(update, { type: EntityTypes.ORG_USER_CERTIFICATE, entity: orgUserCert, persist: false }));
    const entityReducer: IEntityReducer = yield select((state: RootState) => state.entity.present);
    const status = {};
    const name: IName = yield select((state: RootState) => state.session.name);
    // const courseNodeEntities = yield all(
    //   certificate.variableGoals.map((variableGoal: ICourseVariable, index) => {
    //     const entity: Partial<IVerificationRequestEntity> = {
    //       verificationRequestId: id,
    //       id: id - index,
    //       verificationEntityId: variableGoal.id,
    //       name: moduleVariableName(entityReducer, variables[variableGoal.variableId]),
    //       type: variables[variableGoal.variableId].type,
    //       value: variableGoal.value,
    //       result: {
    //         ...previewVariables[variableGoal.variableId],
    //         variableOptionName: variableOptions[previewVariables[variableGoal.variableId]?.variableOptionId]?.name,
    //       } as IVariableResult,
    //       status: compareVariable(
    //         variables[variableGoal.variableId],
    //         previewVariables[variableGoal.variableId],
    //         variableGoal,
    //       )
    //         ? VerificationRequestStatus.COMPLETE
    //         : VerificationRequestStatus.NEW,
    //     };
    //     status[variableGoal.variableId] = entity.status;
    //     return call(update, { type: EntityTypes.VERIFICATION_REQUEST_ENTITY, entity, persist: false });
    //   }),
    // );
    yield put(
      yield call(updatePreview, {
        type: EntityTypes.CERTIFICATE,
        id: courseNode.certificateId,
        data: {
          name,
          certificate,
          status: Object.values(status).some(s => s === VerificationRequestStatus.NEW)
            ? CertificateStatus.PROGRESS
            : CertificateStatus.COMPLETE,
        },
      }),
    );
    // yield all(courseNodeEntities.map(ent => put(ent)));
    // yield put(yield call(generateOfflineId, courseNodeEntities.length));
    yield processNextNode(courseNode.id);
  }

  const feed: IActionFeed = yield select(selectActionFeed);
  const nextItem = feed.feed[feed.index];

  if (nextItem && nextItem.verificationRequestId === id)
    yield put(yield call(updateState, { type: EntityTypes.VERIFICATION_REQUEST, id }));
}

export function compareVariable(variable: IVariable, variablePreview: IPreviewVariable, variableGoal: ICourseVariable) {
  switch (variable.type) {
    case VariableType.BOOLEAN:
      return variablePreview
        ? variableGoal.value === 1
          ? variablePreview.pass
          : !variablePreview.pass
        : !variableGoal.value;
    case VariableType.SCORE:
      return variablePreview && variablePreview.score >= variableGoal.value;
    case VariableType.OPTIONS:
      return variablePreview && variablePreview.variableOptionId === variableGoal.variableOptionId;
  }
}

export function* processVariable(courseNode: ICourseNodeDAO, targetHandleId: number, previousNodeId) {
  let variableId = courseNode.variableId;

  if (!variableId && courseNode.moduleId) {
    const module = yield select((state: RootState) => state.entity.present[EntityTypes.MODULE][courseNode.moduleId]);
    if (module) variableId = module.variableId;
  }

  if (!variableId) return;

  const variable: IVariable = yield select(
    (state: RootState) => state.entity.present[EntityTypes.VARIABLE][variableId],
  );
  const variableRegister: IPreviewVariable = yield select((state: RootState) => state.preview.variables[variableId]);
  const entity: ICourseNodeEntity = courseNode.entities.find(ent => ent.id === targetHandleId);

  if (courseNode.type === CourseNodeType.ASSIGN && courseNode.entityType === CourseNodeRole.VARIABLE) {
    let previousValue: IPreviewVariable = null;

    if (previousNodeId && targetHandleId === Handles.DEFAULT) {
      const previousNode: ICourseNodeDAO = yield select((state: RootState) => selectCourseNode(state, previousNodeId));
      if (previousNode.variableId)
        previousValue = yield select((state: RootState) => state.preview.variables[previousNode.variableId]);
    }

    const data = () => {
      switch (variable.type) {
        case VariableType.BOOLEAN:
          return { pass: targetHandleId === Handles.PASS };
        case VariableType.SCORE:
          //need to handle accumulator
          return {
            score: previousValue
              ? previousValue.score + (variableRegister?.score || 0)
              : entity
              ? operatorValue(entity, variableRegister?.score)
              : null,
          };
        case VariableType.OPTIONS:
          return { variableOptionId: entity.variableOptionId };
      }
    };
    yield put(yield call(updatePreview, { type: EntityTypes.VARIABLE, id: variableId, data: data() }));
    yield processNextNode(courseNode.id);
  } else {
    const variableRegister: IPreviewVariable = yield select((state: RootState) => state.preview.variables[variableId]);

    const outsByHandle = reduceArrayById(courseNode.outs, 'sourceHandle');
    let success = false;

    if (variableRegister)
      switch (variable.type) {
        case VariableType.BOOLEAN:
          if (variableRegister.pass && outsByHandle[Handles.PASS])
            previewCourseNode(outsByHandle[Handles.PASS].targetId);
          if (!variableRegister.pass && outsByHandle[Handles.FAIL]) {
            success = true;
            yield previewCourseNode(outsByHandle[Handles.FAIL].targetId);
          }

          break;
        case VariableType.OPTIONS:
          if (variableRegister.variableOptionId && outsByHandle[`${variableRegister.variableOptionId}`]) {
            success = true;
            yield previewCourseNode(outsByHandle[`${variableRegister.variableOptionId}`].targetId);
          }

          break;
        case VariableType.SCORE:
          const range = courseNode.entities.reduce((prev, ent) => {
            if (ent.minValue < variableRegister.score && ent.maxValue > variableRegister.score)
              prev.push(previewCourseNode(outsByHandle[ent.id].targetId, outsByHandle[ent.id].targetHandle));
            return prev;
          }, []);

          if (range.length) {
            yield all(range);
            success = true;
          }

          break;
      }

    if (!success) yield processNextNode(courseNode.id);
  }
}

function operatorValue(entity: ICourseNodeEntity, value) {
  switch (entity.operator) {
    case -1:
      return value - entity.value;
    case 0:
      return entity.value;
    case 1:
      return value + entity.value;
  }
}

export function* processGroup(courseNode: ICourseNodeDAO, targetHandleId: number) {
  if (courseNode.type === CourseNodeType.ASSIGN) {
    yield put(
      yield call(updatePreview, {
        type: EntityTypes.GROUP,
        id: courseNode.groupId,
        data: { enabled: targetHandleId === Handles.PASS },
      }),
    );
    yield processNextNode(courseNode.id);
  } else if (courseNode.type === CourseNodeType.LOGIC) {
    const groupRegister: IPreviewGroup = yield select((state: RootState) => state.preview.groups[courseNode.groupId]);
    yield all(
      courseNode.entities.reduce((prev, ent) => {
        if (ent.value === Handles.PASS && groupRegister?.enabled)
          return [...prev, ...ent.outs.map(out => previewCourseNode(out.targetId, out.targetHandle))];
        else if ((ent.value === Handles.FAIL && !groupRegister) || groupRegister?.enabled)
          return [...prev, ...ent.outs.map(out => previewCourseNode(out.targetId, out.targetHandle))];

        return prev;
      }, []),
    );
  } else if (courseNode.type === CourseNodeType.FILTER) {
    const groupRegister: IKeyArray<IPreviewGroup> = yield select((state: RootState) => state.preview.groups);
    yield all(
      courseNode.entities.reduce((prev, ent) => {
        if (groupRegister[ent.groupId]?.enabled)
          return [...prev, ...ent.outs.map(out => previewCourseNode(out.targetId, out.targetHandle))];
        return prev;
      }, []),
    );
  }
}

export function* processSystem(courseNode: ICourseNodeDAO) {
  //yield put(yield call(updatePreview, { type: EntityTypes.SYSTEM, id: courseNode.systemId, data: {} }));
  yield processNextNode(courseNode.id);
}

export function* processCertificate(courseNode: ICourseNodeDAO) {
  yield processVerification(courseNode);
  yield put(yield call(updatePreview, { type: EntityTypes.CERTIFICATE, id: courseNode.certificateId, data: {} }));
}

export function* processNodeOut(courseNodeId: number, sourceHandle: number) {
  const id = yield select((state: RootState) => courseNodeId || state.state[EntityTypes.COURSE_NODE]);
  const courseNode: ICourseNodeDAO = yield select((state: RootState) => selectCourseNode(state, id));

  yield all(
    courseNode.outs
      .filter(out => (sourceHandle ? out.sourceHandle === sourceHandle : true))
      .map((out: ICourseOut) => previewCourseNode(out.targetId, out.targetHandle, id)),
  );
}

export function* processNextNode(courseNodeId?: number, sourceHandle?: number) {
  const id = yield select((state: RootState) => courseNodeId || state.state[EntityTypes.COURSE_NODE]);
  const courseNode: ICourseNodeDAO = yield select((state: RootState) => selectCourseNode(state, id));
  const courseNodes = yield select((state: RootState) => state.entity.present[EntityTypes.COURSE_NODE]);

  if (!courseNode || courseNode?.status === -1) return;

  if (courseNode?.outs)
    yield all(
      courseNode.outs
        .filter(out => out.targetId !== courseNodeId && out.sourceHandle === Handles.DEFAULT && out.status > -1)
        .filter(out => courseNodes[out.targetId] && courseNodes[out.targetId].status > -1)
        .map((out: ICourseOut) => previewCourseNode(out.targetId, out.targetHandle, id)),
    );

  if (courseNode?.childId && courseNode?.childId !== courseNodeId) yield previewCourseNode(courseNode.childId);
}
